import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { crmErrorMessage } from "../api/crmErrors";
import { CRM_SETTINGS_BOUNDS } from "../contract";
import useCrmSettings from "../hooks/useCrmSettings";
import CrmPageHeader from "../ui/CrmPageHeader";
import CrmCard from "../ui/CrmCard";
import CrmInput from "../ui/CrmInput";
import CrmSwitch from "../ui/CrmSwitch";
import CrmButton from "../ui/CrmButton";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import { CRM } from "../../../constants/testIds/crm";

/** The screen's sections, in order; each number row names its `settings.crm` field. */
const SECTIONS = [
  { titleKey: "settings.sectionSessions", toggle: true, numbers: [
    { field: "pack_ending_threshold", labelKey: "settings.packEndingLabel", helpKey: "settings.packEndingHelp", unitKey: "settings.packEndingUnit" },
  ] },
  { titleKey: "settings.sectionStages", numbers: [
    { field: "inactivity_days", labelKey: "settings.inactivityLabel", helpKey: "settings.inactivityHelp", unitKey: "settings.inactivityUnit" },
  ] },
  { titleKey: "settings.sectionNudges", numbers: [
    { field: "gone_quiet_weeks", labelKey: "settings.goneQuietLabel", helpKey: "settings.goneQuietHelp", unitKey: "settings.goneQuietUnit" },
    { field: "review_threshold", labelKey: "settings.reviewLabel", helpKey: "settings.reviewHelp", unitKey: "settings.reviewUnit" },
  ] },
  { titleKey: "settings.sectionEmail", numbers: [
    { field: "daily_send_cap", labelKey: "settings.sendCapLabel", helpKey: "settings.sendCapHelp", unitKey: "settings.sendCapUnit" },
  ] },
];

const NUMBER_FIELDS = Object.keys(CRM_SETTINGS_BOUNDS);

/** The editable copy of the server's settings: numbers as the text in their fields. */
const toDraft = (s) => ({
  no_show_consumes_pack: !!s.no_show_consumes_pack,
  ...Object.fromEntries(NUMBER_FIELDS.map((f) => [f, String(s[f])])),
});

/** A whole number inside the field's bounds → the number; anything else → null. */
const parseBounded = (field, text) => {
  const { min, max } = CRM_SETTINGS_BOUNDS[field];
  if (!/^\d+$/.test(String(text).trim())) return null;
  const n = Number(text);
  return n >= min && n <= max ? n : null;
};

/**
 * Admin → CRM → Settings (owner-only — RoleGate at the route): the numbers the CRM's
 * automatic behaviour runs on — whether a no-show uses up a pack session, when a pack
 * counts as ending, when an Active client becomes Past, when a client has gone quiet,
 * when to ask for a review, and the daily marketing email limit.
 *
 * One form, one Save: only the fields that differ from what the server holds are sent
 * (`PUT /api/crm/settings` writes named keys and nothing else), and the server's answer
 * becomes the new baseline. Save stays disabled while nothing changed or a number is out
 * of its bounds (the same bounds the server refuses with 422, `CRM_SETTINGS_BOUNDS`).
 */
export default function CrmSettings() {
  const t = useT("crm");
  const tc = useT("common");
  const { settings, loading, loadError, reload, save } = useCrmSettings();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings) setDraft(toDraft(settings)); }, [settings]);

  const changes = useMemo(() => {
    if (!settings || !draft) return {};
    const out = {};
    if (draft.no_show_consumes_pack !== !!settings.no_show_consumes_pack) {
      out.no_show_consumes_pack = draft.no_show_consumes_pack;
    }
    for (const f of NUMBER_FIELDS) {
      const n = parseBounded(f, draft[f]);
      if (n !== null && n !== settings[f]) out[f] = n;
    }
    return out;
  }, [settings, draft]);

  const invalid = draft ? NUMBER_FIELDS.filter((f) => parseBounded(f, draft[f]) === null) : [];
  const dirty = draft && settings && JSON.stringify(draft) !== JSON.stringify(toDraft(settings));
  const canSave = dirty && invalid.length === 0 && Object.keys(changes).length > 0 && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      await save(changes);
      toast.success(t("settings.saved"));
    } catch (err) {
      toast.error(crmErrorMessage(err, t, tc));
    } finally {
      setSaving(false);
    }
  };

  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  return (
    <div data-testid={CRM.settings} className="space-y-4">
      <CrmPageHeader title={t("settings.title")} subtitle={t("settings.subtitle")} />
      {loadError && <CrmLoadFailure error={loadError} onRetry={reload} testId={CRM.settingsError} />}
      {!loadError && (loading || !draft) && <CrmCard className="animate-pulse h-40" />}

      {!loadError && draft && (
        <form onSubmit={submit} className="space-y-4" noValidate>
          {SECTIONS.map((section) => (
            <CrmCard key={section.titleKey} as="section" className="space-y-5">
              <h2 className="font-display text-xl text-eh-ink">{t(section.titleKey)}</h2>

              {section.toggle && (
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p id="crm-settings-no-show-label" className="text-sm font-medium text-eh-ink">{t("settings.noShowLabel")}</p>
                    <p className="mt-1 max-w-xl text-sm text-eh-muted">{t("settings.noShowHelp")}</p>
                  </div>
                  <CrmSwitch checked={draft.no_show_consumes_pack} onCheckedChange={(v) => set("no_show_consumes_pack", v)}
                    aria-labelledby="crm-settings-no-show-label" data-testid={CRM.settingsField("no_show_consumes_pack")} />
                </div>
              )}

              {section.numbers.map((row) => {
                const bounds = CRM_SETTINGS_BOUNDS[row.field];
                const bad = invalid.includes(row.field);
                const inputId = `crm-settings-${row.field}`;
                const errorId = `${inputId}-error`;
                return (
                  <div key={row.field} className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-[220px] flex-1">
                      <label htmlFor={inputId} className="text-sm font-medium text-eh-ink">{t(row.labelKey)}</label>
                      <p className="mt-1 max-w-xl text-sm text-eh-muted">{t(row.helpKey)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-2">
                        <CrmInput id={inputId} type="number" inputMode="numeric" min={bounds.min} max={bounds.max} step={1}
                          value={draft[row.field]} invalid={bad} className="w-28 text-right"
                          aria-describedby={bad ? errorId : undefined}
                          onChange={(e) => set(row.field, e.target.value)} data-testid={CRM.settingsField(row.field)} />
                        <span className="text-sm text-eh-muted">{t(row.unitKey)}</span>
                      </div>
                      {bad && (
                        <p id={errorId} role="alert" className="text-xs text-red-700" data-testid={CRM.settingsFieldError(row.field)}>
                          {t("settings.invalidNumber", { min: bounds.min, max: bounds.max })}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </CrmCard>
          ))}

          <div className="flex flex-wrap items-center justify-end gap-2">
            {dirty && (
              <CrmButton type="button" variant="ghost" disabled={saving} onClick={() => setDraft(toDraft(settings))}
                data-testid={CRM.settingsDiscard}>
                {t("settings.discard")}
              </CrmButton>
            )}
            <CrmButton type="submit" disabled={!canSave} data-testid={CRM.settingsSave}>
              {t("settings.save")}
            </CrmButton>
          </div>
        </form>
      )}
    </div>
  );
}
